import { useState, useEffect } from 'react'
import { Loader2, AlertCircle, Download, ZoomIn, ZoomOut, RotateCw } from 'lucide-react'
import { api } from '@/api/client'

interface DocumentPreviewProps {
  documentId: string
  contentType: string
  fileName: string
}

export function DocumentPreview({ documentId, contentType, fileName }: DocumentPreviewProps) {
  const [url, setUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    api.getDocumentPreview(documentId)
      .then((res) => {
        if (!cancelled && res) {
          setUrl(res.url)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load preview')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [documentId])

  const isImage = contentType.startsWith('image/')
  const isPdf = contentType === 'application/pdf'

  const handleDownload = () => {
    const downloadUrl = api.getLibraryDocumentDownloadUrl(documentId)
    window.open(downloadUrl, '_blank')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-[#F7FAFC]">
        <div className="flex flex-col items-center gap-2">
          <Loader2 size={24} className="text-[#1B365D] animate-spin" />
          <span className="text-[13px] text-[#718096]">Loading preview...</span>
        </div>
      </div>
    )
  }

  if (error || !url) {
    return (
      <div className="flex items-center justify-center h-full bg-[#F7FAFC]">
        <div className="flex flex-col items-center gap-2">
          <AlertCircle size={24} className="text-red-400" />
          <span className="text-[13px] text-[#718096]">{error || 'Preview not available'}</span>
          <button
            onClick={handleDownload}
            className="text-[13px] text-[#1B365D] hover:underline flex items-center gap-1 mt-2"
          >
            <Download size={14} />
            Download instead
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      {isImage && (
        <div className="flex items-center gap-1 p-2 border-b border-[#E2E8F0] bg-white">
          <button
            onClick={() => setZoom(z => Math.max(0.25, z - 0.25))}
            className="p-1.5 text-[#718096] hover:text-[#1B365D] rounded hover:bg-[#EDF2F7]"
            title="Zoom out"
          >
            <ZoomOut size={16} />
          </button>
          <span className="text-[12px] text-[#718096] min-w-[40px] text-center">
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={() => setZoom(z => Math.min(4, z + 0.25))}
            className="p-1.5 text-[#718096] hover:text-[#1B365D] rounded hover:bg-[#EDF2F7]"
            title="Zoom in"
          >
            <ZoomIn size={16} />
          </button>
          <button
            onClick={() => setRotation(r => (r + 90) % 360)}
            className="p-1.5 text-[#718096] hover:text-[#1B365D] rounded hover:bg-[#EDF2F7]"
            title="Rotate"
          >
            <RotateCw size={16} />
          </button>
          <div className="flex-1" />
          <button
            onClick={handleDownload}
            className="p-1.5 text-[#718096] hover:text-[#1B365D] rounded hover:bg-[#EDF2F7]"
            title="Download"
          >
            <Download size={16} />
          </button>
        </div>
      )}

      {/* Preview area */}
      <div className="flex-1 overflow-auto bg-[#F1F5F9] flex items-center justify-center p-4">
        {isImage && (
          <img
            src={url}
            alt={fileName}
            className="max-w-full transition-transform"
            style={{
              transform: `scale(${zoom}) rotate(${rotation}deg)`,
              transformOrigin: 'center',
            }}
          />
        )}
        {isPdf && (
          <iframe
            src={url}
            title={fileName}
            className="w-full h-full border-0 rounded"
          />
        )}
        {!isImage && !isPdf && (
          <div className="text-center">
            <p className="text-[14px] text-[#718096] mb-3">Preview not available for this file type</p>
            <button
              onClick={handleDownload}
              className="text-[13px] text-[#1B365D] hover:underline flex items-center gap-1 mx-auto"
            >
              <Download size={14} />
              Download file
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
